import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import TableV2 from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import { useAuth } from "../../../../context/auth-context";

/** ---------------------- Tipos ---------------------- */

type HeaderMode = "retorno" | "tipo";

type SaidasServicoConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;

  title: string;
  reportTitle: string;
  fileNamePrefix: string;

  headerMode: HeaderMode;
  enableRetornoModal: boolean;
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

export type SaidaItemAPI = {
  id: number | string;
  movimentacao_id: number | string;
  produto_id: number | string;
  estoque_id?: number | string | null;
  quantidade: number | string;
  custo_unitario: string | number | null;
  moeda?: string | null;
  produto: {
    id: number | string;
    codigo: string;
    nome: string;
    ncm?: string | null;
    classificacao?: string | null;
    posicao: string | null;
    peso?: string | number | null;
    dimensao?: string | null;
    ocultar?: boolean | null;
  };
};

export type RetornoSolicitacaoAPI = {
  id: number | string | null;
  status: "solicitado" | "aprovado" | "negado" | null;
  quantidade: number | null;
  motivo: string | null;
  observacao: string | null;
  motivo_negativa: string | null;
  criado_em: string | null;
  analisado_em: string | null;
};

export type SaidaMovAPI = {
  movimentacao: {
    id: number | string;
    empresa_id?: number | string | null;
    estoque_id?: number | string | null;
    tipo_movimentacao_id?: number | string | null;
    codigo_movimentacao?: string | null;

    tipo?: "servico" | "venda" | "consumo" | "perda" | "outro" | string | null;
    tipo_full?: string | null;

    responsavel_id: number | string | null;
    responsavel_nome: string | null;

    os_numero?: string | null;
    coletor_nome?: string | null;
    embarcacao?: string | null;
    cliente_nome?: string | null;
    empresa_cliente?: string | null;
    proposta_venda?: string | null;

    custo_total?: string | number | null;
    motivo?: string | null;
    origem_mov_id?: number | string | null;
    coordenador_responsavel_ids?: unknown;
    criado_em: string;

    tipo_movimentacao?: {
      codigo?: string | null;
      nome?: string | null;
      natureza?: string | null;
      sinal?: number | string | null;
    };

    retorno_solicitacao?: RetornoSolicitacaoAPI | null;

    estoque?: {
      id?: number | string | null;
      codigo?: string | null;
      nome?: string | null;
      modulo_id?: number | string | null;
    };

    modulo?: {
      id?: number | string | null;
      codigo?: string | null;
      label?: string | null;
    };
  };
  itens: SaidaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  fonte?: "cache" | "banco";
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  meta: {
    filtros: {
      tipo_saida?: string | null;
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC" | "asc" | "desc";
      limit: number;
      offset: number;
    };
    count: number;
    total?: number;
    has_more?: boolean;
  };
  data: SaidaMovAPI[];
};

type SaidaRowUI = string[];

type RetornoStatusUI = "none" | "solicitado" | "aprovado" | "negado";

type SaidaRowMeta = {
  movimentacaoId: number;
  produtoId: number;
  itemId: number;

  osNumero: string;
  tipo: string;
  tipoFull: string;

  codigo: string;
  nomeProduto: string;

  quantidade: number;
  custoUnitario: number;
  moeda: string;

  tecnico: string;

  responsavelId: number | null;
  responsavelNome: string | null;

  retornoSolicitacaoId: number | null;
  retornoStatus: RetornoStatusUI;
  retornoQuantidade: number | null;
  retornoMotivo: string | null;
  retornoObservacao: string | null;
  retornoMotivoNegativa: string | null;
  retornoCriadoEm: string | null;
  retornoAnalisadoEm: string | null;
};

type SaidasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

type ItemOSModal = {
  originalIndex: number;
  row: SaidaRowUI;
  meta: SaidaRowMeta;
};

type ModalState = {
  open: boolean;
  os: string;
  itens: ItemOSModal[];
};

type SelecoesState = Record<
  number,
  {
    marcado: boolean;
    quantidade: string;
  }
>;

/** ---------------------- Constantes ---------------------- */

const URL_SAIDAS =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-saidas/servicos";

const POLL_FALLBACK_MS = 15000;
const ACCESS_TOKEN_KEY = "access_token";

/**
 * Estes POSTs ainda não foram migrados no fluxo novo.
 * Quando criarmos post-retorno-solicitar-any e post-retorno-cancelar-any,
 * basta trocar as funções solicitarRetornoOS/cancelarSolicitacao.
 */
const RETORNO_POSTS_MIGRADOS = false;

/** ---------------------- Config / sessão ---------------------- */

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): SaidasServicoConfig {
  const area = normalizeSlug(areaSlug) || "suprimentos";
  const estoque = normalizeSlug(estoqueSlug) || "estoque";

  const estoqueTitle = slugToTitle(estoque);

  const isUsoCorporativo = estoque === "estoque-p-uso-coorporativo";
  const isTecnicos = estoque === "estoque-p-tecnicos-e-laboratorio";

  return {
    areaSlug: area,
    estoqueSlug: estoque,
    key: `${area}-${estoque}-saidas-servicos`,

    title: `${slugToTitle(area)} - Saídas Serviços - ${estoqueTitle}`,
    reportTitle: `Relatório de Saídas por Serviço - ${estoqueTitle}`,
    fileNamePrefix: `${area}-${estoque}-Saidas-Servicos`,

    headerMode: isUsoCorporativo || isTecnicos ? "tipo" : "retorno",

    /**
     * Mantive o modal visualmente disponível para estoques de serviço.
     * A ação de solicitar/cancelar retorno fica bloqueada até migrarmos os POSTs.
     */
    enableRetornoModal: !isUsoCorporativo && !isTecnicos,
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  config: SaidasServicoConfig
): ModuloAtual | null {
  const areaSlug = normalizeComparable(config.areaSlug);
  const estoqueSlug = normalizeComparable(config.estoqueSlug);

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoqueSlug] || estoqueSlug;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (areaSlug && depSlug && depSlug !== areaSlug) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoqueSlug ||
            modLabel === estoqueSlug ||
            modCodigo.includes(estoqueSlug) ||
            estoqueSlug.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return {
        data: iso,
        hora: "",
      };
    }

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return { data, hora };
  } catch {
    return { data: iso, hora: "" };
  }
}

function formatMoney(valor: number, moeda?: string | null): string {
  const currency = String(moeda || "BRL").toUpperCase();

  const symbol =
    currency === "USD"
      ? "$"
      : currency === "EUR"
      ? "€"
      : currency === "BRL"
      ? "R$"
      : currency;

  return `${symbol} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function parseQuantidade(value: unknown): number {
  const n = Number(String(value ?? "0").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function parseCurrency(value: unknown): number {
  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function normalizeRetornoStatus(
  status: RetornoSolicitacaoAPI["status"] | undefined | null
): RetornoStatusUI {
  if (status === "solicitado") return "solicitado";
  if (status === "aprovado") return "aprovado";
  if (status === "negado") return "negado";
  return "none";
}

function isSaidaServico(mov: SaidaMovAPI["movimentacao"]): boolean {
  const tipoFull = String(mov.tipo_full ?? "").toLowerCase();
  const tipo = String(mov.tipo ?? "").toLowerCase();
  const codigoMov = String(mov.codigo_movimentacao ?? "").toUpperCase();
  const tipoMovCodigo = String(
    mov.tipo_movimentacao?.codigo ?? ""
  ).toUpperCase();

  if (codigoMov === "SAI-SERVICO") return true;
  if (tipoMovCodigo === "SAI-SERVICO") return true;
  if (tipoFull === "standby-servico-saida") return true;
  if (tipoFull.includes("servico") && tipoFull.includes("saida")) return true;
  if (tipoFull.includes("serviço") && tipoFull.includes("saida")) return true;
  if (tipo === "servico" || tipo === "serviço") return true;

  return false;
}

function getHeaders(config: SaidasServicoConfig): string[] {
  if (config.headerMode === "tipo") {
    return [
      "OS",
      "Tipo",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Moeda",
      "Técnico",
      "Data",
      "Hora",
    ];
  }

  return [
    "OS",
    "Código",
    "Nome do Produto",
    "Quantidade",
    "Custo Unitário",
    "Moeda",
    "Técnico",
    "C. Responsável",
    "Data",
    "Hora",
  ];
}

function getColumnWidths(config: SaidasServicoConfig): string[] {
  if (config.headerMode === "tipo") {
    return [
      "60px",
      "80px",
      "70px",
      "300px",
      "50px",
      "90px",
      "60px",
      "90px",
      "90px",
      "60px",
    ];
  }

  return [
    "60px",
    "70px",
    "300px",
    "50px",
    "90px",
    "60px",
    "90px",
    "140px",
    "90px",
    "60px",
  ];
}

function toNumberId(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) ? n : 0;
}

function flattenToRows(
  movs: SaidaMovAPI[],
  config: SaidasServicoConfig
): {
  rows: SaidaRowUI[];
  rowMeta: SaidaRowMeta[];
} {
  const rows: SaidaRowUI[] = [];
  const rowMeta: SaidaRowMeta[] = [];

  for (const mov of movs || []) {
    if (!isSaidaServico(mov.movimentacao)) continue;

    const { data, hora } = toBRT(mov.movimentacao?.criado_em || "");

    const os = mov.movimentacao?.os_numero || "-";
    const tipo = mov.movimentacao?.tipo || "-";
    const tipoFull = mov.movimentacao?.tipo_full || "-";
    const tecnico = mov.movimentacao?.coletor_nome || "-";
    const responsavel = mov.movimentacao?.responsavel_nome || "-";

    const retorno = mov.movimentacao?.retorno_solicitacao || null;
    const retornoStatus = normalizeRetornoStatus(retorno?.status);

    for (const item of mov.itens || []) {
      const quantidade = parseQuantidade(item.quantidade);
      const custo = parseQuantidade(item.custo_unitario);
      const moeda = String(item.moeda || "BRL").toUpperCase();

      const codigo = String(item.produto?.codigo ?? "").toUpperCase();
      const nomeProduto = String(item.produto?.nome ?? "").toUpperCase();

      if (config.headerMode === "tipo") {
        rows.push([
          os,
          String(tipo),
          codigo,
          nomeProduto,
          String(Number.isFinite(quantidade) ? quantidade : 0),
          formatMoney(Number.isFinite(custo) ? custo : 0, moeda),
          moeda,
          tecnico,
          data,
          hora,
        ]);
      } else {
        rows.push([
          os,
          codigo,
          nomeProduto,
          String(Number.isFinite(quantidade) ? quantidade : 0),
          formatMoney(Number.isFinite(custo) ? custo : 0, moeda),
          moeda,
          tecnico,
          responsavel,
          data,
          hora,
        ]);
      }

      rowMeta.push({
        movimentacaoId: toNumberId(mov.movimentacao.id),
        produtoId: toNumberId(item.produto.id),
        itemId: toNumberId(item.id),

        osNumero: os,
        tipo: String(tipo),
        tipoFull: String(tipoFull),

        codigo,
        nomeProduto,

        quantidade: Number.isFinite(quantidade) ? quantidade : 0,
        custoUnitario: Number.isFinite(custo) ? custo : 0,
        moeda,

        tecnico,

        responsavelId:
          mov.movimentacao.responsavel_id === null ||
          mov.movimentacao.responsavel_id === undefined
            ? null
            : toNumberId(mov.movimentacao.responsavel_id),
        responsavelNome: mov.movimentacao.responsavel_nome,

        retornoSolicitacaoId:
          retorno?.id === null || retorno?.id === undefined
            ? null
            : toNumberId(retorno.id),
        retornoStatus,
        retornoQuantidade: retorno?.quantidade ?? null,
        retornoMotivo: retorno?.motivo ?? null,
        retornoObservacao: retorno?.observacao ?? null,
        retornoMotivoNegativa: retorno?.motivo_negativa ?? null,
        retornoCriadoEm: retorno?.criado_em ?? null,
        retornoAnalisadoEm: retorno?.analisado_em ?? null,
      });
    }
  }

  return {
    rows,
    rowMeta,
  };
}

/** ---------------------- Fetchers ---------------------- */

async function fetchSaidasOnce(
  moduloAtual: ModuloAtual | null,
  query?: SaidasQuery
): Promise<{
  rows: SaidaRowUI[];
  rowMeta: SaidaRowMeta[];
  meta: RespAPI["meta"] | null;
  resp: RespAPI | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const payload = {
    modulo_id: moduloAtual.id,
    tipo_saida: "servico",
    order: query?.order ?? "desc",
    limit: query?.limit ?? 10000,
    offset: query?.offset ?? 0,
    ...(query?.date_from ? { date_from: query.date_from } : {}),
    ...(query?.date_to ? { date_to: query.date_to } : {}),
  };

  const res = await fetch(URL_SAIDAS, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const parsedJson = (await res.json().catch(() => null)) as RespAPI | null;

  if (!res.ok || parsedJson?.ok === false) {
    const erro =
      (parsedJson as any)?.erro ||
      (Array.isArray((parsedJson as any)?.erros) &&
        (parsedJson as any).erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  const dataRaw: SaidaMovAPI[] =
    parsedJson && Array.isArray(parsedJson.data) ? parsedJson.data : [];

  const flattened = flattenToRows(dataRaw, getConfigByParams());

  const meta: RespAPI["meta"] | null =
    parsedJson && parsedJson.meta ? parsedJson.meta : null;

  return {
    rows: flattened.rows,
    rowMeta: flattened.rowMeta,
    meta,
    resp: parsedJson,
  };
}

/** ---------------------- Hook genérico ---------------------- */

function useSaidasServicoGenerico(
  moduloAtual: ModuloAtual | null,
  authLoading: boolean,
  config: SaidasServicoConfig,
  query?: SaidasQuery
) {
  const [rows, setRows] = useState<SaidaRowUI[]>([]);
  const [rowMeta, setRowMeta] = useState<SaidaRowMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [resp, setResp] = useState<RespAPI | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      if (!moduloAtual?.id) {
        throw new Error("Módulo de estoque não identificado para esta rota");
      }

      const payload = {
        modulo_id: moduloAtual.id,
        tipo_saida: "servico",
        order: query?.order ?? "desc",
        limit: query?.limit ?? 10000,
        offset: query?.offset ?? 0,
        ...(query?.date_from ? { date_from: query.date_from } : {}),
        ...(query?.date_to ? { date_to: query.date_to } : {}),
      };

      const res = await fetch(URL_SAIDAS, {
        method: "POST",
        headers: buildAuthHeaders(true),
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      const parsedJson = (await res.json().catch(() => null)) as RespAPI | null;

      if (!res.ok || parsedJson?.ok === false) {
        const erro =
          (parsedJson as any)?.erro ||
          (Array.isArray((parsedJson as any)?.erros) &&
            (parsedJson as any).erros.join(" | ")) ||
          `HTTP ${res.status}`;

        throw new Error(erro);
      }

      const dataRaw: SaidaMovAPI[] =
        parsedJson && Array.isArray(parsedJson.data) ? parsedJson.data : [];

      const flattened = flattenToRows(dataRaw, config);

      setRows(flattened.rows);
      setRowMeta(flattened.rowMeta);
      setMeta(parsedJson?.meta ?? null);
      setResp(parsedJson);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar saídas.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    setLoading(true);
    setErro(null);
    setRows([]);
    setRowMeta([]);
    setMeta(null);
    setResp(null);

    if (authLoading) {
      return () => {
        stop.current = true;
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
      };
    }

    (async () => {
      try {
        await carregar();
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(jitter(POLL_FALLBACK_MS));

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useSaidasServicoGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading, config.key, JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    rowMeta,
    loading,
    refresh,
    meta,
    resp,
    erro,
  };
}

/** ---------------------- Modal styles ---------------------- */

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1080,
  background: "#ffffff",
  borderRadius: 0,
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  border: "1px solid #0c2b53",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #0c2b53",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#111827",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #0c2b53",
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  background: "#FFFFFF",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#ffffff",
  color: "#111827",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#0c2b53",
  color: "#ffffff",
  borderRadius: 0,
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonMutedStyle: React.CSSProperties = {
  border: "1px solid #0c2b53",
  background: "#f8fafc",
  color: "#0c2b53",
  borderRadius: 0,
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const buttonDangerStyle: React.CSSProperties = {
  border: "1px solid #7D1A1A",
  background: "#ffffff",
  color: "#7D1A1A",
  borderRadius: 0,
  padding: "8px 12px",
  fontSize: 13,
  cursor: "pointer",
};

const badgeBaseStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #0c2b53",
  borderRadius: 0,
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 100,
  resize: "vertical",
  fontFamily: "inherit",
};

function getStatusLabel(status: RetornoStatusUI) {
  switch (status) {
    case "solicitado":
      return "p/ retorno";
    case "aprovado":
      return "Aprovado";
    case "negado":
      return "Negado";
    default:
      return "Sem solicitação";
  }
}

function getStatusBadgeStyle(status: RetornoStatusUI): React.CSSProperties {
  switch (status) {
    case "solicitado":
      return {
        ...badgeBaseStyle,
        background: "#FFD6CD",
        color: "#92400e",
        border: "1px solid #060606",
      };
    case "aprovado":
      return {
        ...badgeBaseStyle,
        background: "#dcfce7",
        color: "#166534",
        border: "1px solid #86efac",
      };
    case "negado":
      return {
        ...badgeBaseStyle,
        background: "#f8d7da",
        color: "#991b1b",
        border: "1px solid #f5a3ad",
      };
    default:
      return {
        ...badgeBaseStyle,
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
      };
  }
}

function canRequestStatus(status: RetornoStatusUI) {
  return status === "none" || status === "negado";
}

function canCancelStatus(status: RetornoStatusUI) {
  return status === "solicitado";
}

/** ---------------------- Página ---------------------- */

const SaidasServicoGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const { sessao, loading: authLoading } = useAuth();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const moduloAtual = useMemo(
    () => findModuloAtualFromSessao(sessao, config),
    [sessao, config]
  );

  const headers = useMemo(() => getHeaders(config), [config]);
  const columnWidths = useMemo(() => getColumnWidths(config), [config]);

  const { rows, rowMeta, loading, refresh, erro, resp } =
    useSaidasServicoGenerico(moduloAtual, authLoading, config);

  const [modal, setModal] = useState<ModalState>({
    open: false,
    os: "",
    itens: [],
  });

  const [selecao, setSelecao] = useState<SelecoesState>({});
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cancelandoIds, setCancelandoIds] = useState<number[]>([]);

  const pageLoading = authLoading || loading;

  const title = useMemo(() => {
    const estoqueNome = resp?.estoque?.nome;
    const moduloLabel = resp?.modulo?.label || moduloAtual?.label;

    if (estoqueNome)
      return `${slugToTitle(
        config.areaSlug
      )} - Saídas Serviços - ${estoqueNome}`;

    if (moduloLabel)
      return `${slugToTitle(
        config.areaSlug
      )} - Saídas Serviços - ${moduloLabel}`;

    return config.title;
  }, [config.areaSlug, config.title, resp, moduloAtual]);

  const reportTitle = useMemo(() => {
    const estoqueNome = resp?.estoque?.nome;

    if (estoqueNome) {
      return `Relatório de Saídas por Serviço - ${estoqueNome}`;
    }

    return config.reportTitle;
  }, [config.reportTitle, resp]);

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const totalProdutos = useMemo(() => {
    const quantidadeIndex = headers.indexOf("Quantidade");

    if (quantidadeIndex < 0) return 0;

    return rows.reduce((acc, row) => {
      return acc + parseQuantidade(row[quantidadeIndex]);
    }, 0);
  }, [headers, rows]);

  const totaisPorMoeda = useMemo(() => {
    const quantidadeIndex = headers.indexOf("Quantidade");
    const custoIndex = headers.indexOf("Custo Unitário");
    const moedaIndex = headers.indexOf("Moeda");

    const acc: Record<string, number> = {};

    if (quantidadeIndex < 0 || custoIndex < 0) return acc;

    rows.forEach((row) => {
      const quantidade = parseQuantidade(row[quantidadeIndex]);
      const custo = parseCurrency(row[custoIndex]);
      const moeda =
        moedaIndex >= 0
          ? String(row[moedaIndex] || "BRL").toUpperCase()
          : "BRL";

      const incremento =
        Number.isFinite(quantidade) && Number.isFinite(custo)
          ? quantidade * custo
          : 0;

      if (!acc[moeda]) acc[moeda] = 0;

      acc[moeda] += incremento;
    });

    return acc;
  }, [headers, rows]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Itens Baixados: ${totalProdutos}`);

    Object.entries(totaisPorMoeda)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([moeda, valor]) => {
        lines.push(
          `Valor Total das Baixas (${moeda}): ${formatMoney(valor, moeda)}`
        );
      });

    return lines;
  }, [totalProdutos, totaisPorMoeda]);

  const gerarRelatorioSaidas = useCallback(() => {
    generatePdfReport({
      reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações das Baixas de Serviço",
      headers,
      rows,
      custoColIndex: headers.indexOf("Custo Unitário"),
      enabledColumns: enabledColumnIndexes,
      footerLines,
    });
  }, [
    reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    footerLines,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const baseSaidas = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas`
      );

      const baseServicos = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/saidas/servicos`
      );

      const isSaidasContext =
        currentPath.includes(baseSaidas) ||
        currentPath.includes(baseServicos) ||
        eventPath.includes(baseSaidas) ||
        eventPath.includes(baseServicos) ||
        currentPath.includes("saidas") ||
        eventPath.includes("saidas") ||
        moduloLabel.includes("saidas") ||
        moduloLabel.includes("servicos") ||
        moduloLabel.includes("serviços") ||
        moduloCodigo.includes("saidas") ||
        moduloCodigo.includes("servicos") ||
        moduloSlug.includes("saidas") ||
        moduloSlug.includes("servicos");

      if (!isSaidasContext) return;

      gerarRelatorioSaidas();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioSaidas]);

  const openModalPorOS = (os: string) => {
    if (!config.enableRetornoModal) return;

    const itensDaOS: ItemOSModal[] = rows
      .map((row, index) => {
        const meta = rowMeta[index];

        if (!meta) return null;
        if ((meta.osNumero || row[0] || "") !== os) return null;

        return {
          originalIndex: index,
          row,
          meta,
        };
      })
      .filter(Boolean) as ItemOSModal[];

    const initialSelecao: SelecoesState = {};

    for (const item of itensDaOS) {
      const permitido = canRequestStatus(item.meta.retornoStatus);

      initialSelecao[item.originalIndex] = {
        marcado: permitido,
        quantidade: String(Math.abs(item.meta.quantidade) || ""),
      };
    }

    setModal({
      open: true,
      os,
      itens: itensDaOS,
    });

    setSelecao(initialSelecao);
    setMotivo("");
  };

  const closeModal = () => {
    if (submitting || cancelandoIds.length > 0) return;

    setModal({
      open: false,
      os: "",
      itens: [],
    });

    setSelecao({});
    setMotivo("");
  };

  const itensSolicitaveis = useMemo(() => {
    return modal.itens.filter((item) =>
      canRequestStatus(item.meta.retornoStatus)
    );
  }, [modal.itens]);

  const itensSelecionados = useMemo(() => {
    return itensSolicitaveis.filter(
      (item) => selecao[item.originalIndex]?.marcado
    );
  }, [itensSolicitaveis, selecao]);

  const itensComSolicitacaoPendente = useMemo(() => {
    return modal.itens.filter((item) =>
      canCancelStatus(item.meta.retornoStatus)
    );
  }, [modal.itens]);

  const marcarTodos = () => {
    setSelecao((prev) => {
      const next = { ...prev };

      for (const item of itensSolicitaveis) {
        next[item.originalIndex] = {
          marcado: true,
          quantidade:
            prev[item.originalIndex]?.quantidade ||
            String(Math.abs(item.meta.quantidade) || ""),
        };
      }

      return next;
    });
  };

  const desmarcarTodos = () => {
    setSelecao((prev) => {
      const next = { ...prev };

      for (const item of itensSolicitaveis) {
        next[item.originalIndex] = {
          marcado: false,
          quantidade:
            prev[item.originalIndex]?.quantidade ||
            String(Math.abs(item.meta.quantidade) || ""),
        };
      }

      return next;
    });
  };

  const toggleItem = (originalIndex: number, checked: boolean) => {
    setSelecao((prev) => ({
      ...prev,
      [originalIndex]: {
        ...prev[originalIndex],
        marcado: checked,
      },
    }));
  };

  const setQuantidadeItem = (originalIndex: number, value: string) => {
    setSelecao((prev) => ({
      ...prev,
      [originalIndex]: {
        ...prev[originalIndex],
        quantidade: value,
      },
    }));
  };

  async function solicitarRetornoOS() {
    if (!RETORNO_POSTS_MIGRADOS) {
      alert(
        "A listagem de saídas já está no novo schema. A solicitação de retorno ainda precisa ser migrada para um endpoint any antes de ser usada."
      );
      return;
    }

    if (!itensSelecionados.length) {
      alert("Selecione ao menos um item para solicitar retorno.");
      return;
    }

    for (const item of itensSelecionados) {
      const quantidade = Number(selecao[item.originalIndex]?.quantidade);
      const quantidadeMaxima = Math.abs(item.meta.quantidade);

      if (!Number.isFinite(quantidade) || quantidade <= 0) {
        alert(
          `Informe uma quantidade válida para o item ${item.meta.nomeProduto}.`
        );
        return;
      }

      if (quantidade > quantidadeMaxima) {
        alert(
          `A quantidade do item ${item.meta.nomeProduto} não pode ser maior que ${quantidadeMaxima}.`
        );
        return;
      }
    }

    setSubmitting(true);

    try {
      await refresh();
      closeModal();
    } catch (e: any) {
      alert(e?.message || "Falha ao solicitar retorno.");
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelarSolicitacao(item: ItemOSModal) {
    if (!RETORNO_POSTS_MIGRADOS) {
      alert(
        "A listagem de saídas já está no novo schema. O cancelamento de retorno ainda precisa ser migrado para um endpoint any antes de ser usado."
      );
      return;
    }

    const solicitacaoId = item.meta.retornoSolicitacaoId;

    if (!solicitacaoId) {
      alert("Solicitação não encontrada para este item.");
      return;
    }

    const confirmar = window.confirm(
      `Cancelar a solicitação de retorno do item "${item.meta.nomeProduto}"?`
    );

    if (!confirmar) return;

    setCancelandoIds((prev) => [...prev, item.originalIndex]);

    try {
      await refresh();

      setModal((prev) => ({
        ...prev,
        itens: prev.itens.map((x) =>
          x.originalIndex === item.originalIndex
            ? {
                ...x,
                meta: {
                  ...x.meta,
                  retornoSolicitacaoId: null,
                  retornoStatus: "none",
                },
              }
            : x
        ),
      }));
    } catch (e: any) {
      alert(e?.message || "Falha ao cancelar solicitação.");
    } finally {
      setCancelandoIds((prev) =>
        prev.filter((id) => id !== item.originalIndex)
      );
    }
  }

  const renderModal = () => {
    if (!modal.open) return null;

    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={modalHeaderStyle}>
            <h2 style={modalTitleStyle}>Solicitar retorno da OS {modal.os}</h2>

            <button
              type="button"
              onClick={closeModal}
              disabled={submitting || cancelandoIds.length > 0}
              style={buttonSecondaryStyle}
            >
              Fechar
            </button>
          </div>

          <div style={modalBodyStyle}>
            {!RETORNO_POSTS_MIGRADOS && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "10px 12px",
                  border: "1px solid #e7cf9a",
                  background: "#fff8e8",
                  color: "#6b4f1f",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                A consulta de saídas já está no novo schema. As ações de
                solicitar/cancelar retorno ainda aguardam migração dos endpoints
                POST.
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
                gap: 8,
              }}
            >
              <div style={{ fontWeight: 700, color: "#0c2b53" }}>
                Itens da OS
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={marcarTodos}
                  style={buttonMutedStyle}
                >
                  Marcar todos
                </button>

                <button
                  type="button"
                  onClick={desmarcarTodos}
                  style={buttonMutedStyle}
                >
                  Desmarcar todos
                </button>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Selecionar
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Código
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Produto
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Qtd. Original
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Qtd. Retorno
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Status
                    </th>
                    <th style={{ border: "1px solid #0c2b53", padding: 8 }}>
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {modal.itens.map((item) => {
                    const permitido = canRequestStatus(item.meta.retornoStatus);
                    const podeCancelar = canCancelStatus(
                      item.meta.retornoStatus
                    );
                    const cancelando = cancelandoIds.includes(
                      item.originalIndex
                    );

                    return (
                      <tr
                        key={`${item.meta.movimentacaoId}-${item.meta.produtoId}`}
                      >
                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          <input
                            type="checkbox"
                            checked={
                              selecao[item.originalIndex]?.marcado === true
                            }
                            disabled={!permitido}
                            onChange={(e) =>
                              toggleItem(item.originalIndex, e.target.checked)
                            }
                          />
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {item.meta.codigo}
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {item.meta.nomeProduto}
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {Math.abs(item.meta.quantidade)}
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          <input
                            type="number"
                            min={1}
                            max={Math.abs(item.meta.quantidade)}
                            disabled={!permitido}
                            value={
                              selecao[item.originalIndex]?.quantidade ?? ""
                            }
                            onChange={(e) =>
                              setQuantidadeItem(
                                item.originalIndex,
                                e.target.value
                              )
                            }
                            style={{
                              ...inputStyle,
                              height: 34,
                              padding: "6px 8px",
                            }}
                          />
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          <span
                            style={getStatusBadgeStyle(item.meta.retornoStatus)}
                          >
                            {getStatusLabel(item.meta.retornoStatus)}
                          </span>
                        </td>

                        <td style={{ border: "1px solid #0c2b53", padding: 8 }}>
                          {podeCancelar ? (
                            <button
                              type="button"
                              onClick={() => cancelarSolicitacao(item)}
                              disabled={cancelando}
                              style={buttonDangerStyle}
                            >
                              {cancelando ? "Cancelando..." : "Cancelar"}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 16 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0c2b53",
                  marginBottom: 6,
                }}
              >
                Motivo do retorno
              </label>

              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Informe o motivo da solicitação de retorno."
                style={textareaStyle}
              />
            </div>
          </div>

          <div style={modalFooterStyle}>
            <div style={{ fontSize: 13, color: "#475569" }}>
              {itensSelecionados.length} item(ns) selecionado(s).{" "}
              {itensComSolicitacaoPendente.length > 0
                ? `${itensComSolicitacaoPendente.length} item(ns) com solicitação pendente.`
                : ""}
            </div>

            <button
              type="button"
              onClick={solicitarRetornoOS}
              disabled={submitting || itensSelecionados.length === 0}
              style={buttonPrimaryStyle}
            >
              {submitting ? "Solicitando..." : "Solicitar Retorno"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <TitleButtons fullBleedLine title={title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {pageLoading ? (
        <div style={{ padding: 16 }}>Carregando saídas…</div>
      ) : (
        <TableV2
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          isTableStandby
          onDoubleClick={(_viewIndex, rowData) => {
            if (!config.enableRetornoModal) return;

            const os = String(rowData[0] ?? "").trim();

            if (!os || os === "-") return;

            openModalPorOS(os);
          }}
        />
      )}

      {renderModal()}
    </div>
  );
};

export default SaidasServicoGenerico;
